import React, { useEffect, useRef, useState } from "react";
import "./videoUploader.scss";
import { AlertService, axiosInstance, http } from "../../fx-core";
import Header from "../../home/Header/Header";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  IconButton
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";



interface PresignResponse {
  url: string;
  key: string;
  expiresAtUtc: string;
  method: string;
  headers: Record<string, string>;
}



interface VideoRecord {
  id: string;
  userId: number;
  stallId: number;
  title: string;
  description: string;
  s3Url: string;
  thumbnailUrl?: string;
  hlsKey: string;
  likes: number;
  uploadedDate: string;
  views: number;
  stallName?: string;
}



interface VideoUploadProps {
  userId: number;
  stallId: number;
  onClose?: () => void;
}



/**
 * Safe presign extractor — accepts common variants used by different environments.
 */
const getPresign = (data: any) => {
  return {
    url:
      data?.url ||
      data?.presignedUrl ||
      data?.Url ||
      data?.PresignedUrl ||
      "",
    key: data?.key || data?.Key || ""
  };
};


// Cloud Upload Icon Component
const CloudUploadIcon = () => (
  <svg
    width="42"
    height="42"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#C6C9CF" }}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);


// Small Upload Icon for Thumbnail
const SmallUploadIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#C6C9CF" }}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);


// Edit Icon (Black)
const EditIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);


// Delete Icon (Black)
const DeleteIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

// Heart Icon (Transparent)
const HeartIcon = ({ size = 16, color = "#666" }: { size?: number; color?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.8"
  >
    <path d="M12 21s-7.5-4.6-9.5-8.6A5.5 5.5 0 018.6 6c1.5 0 2.8.7 3.4 1.8C12.6 6.7 14 6 15.4 6a5.5 5.5 0 016.1 6.4C19.5 16.4 12 21 12 21z" />
  </svg>
);



const VideoUpload: React.FC<VideoUploadProps> = ({ userId, stallId, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videos, setVideos] = useState<VideoRecord[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [editingVideo, setEditingVideo] = useState<VideoRecord | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editThumbnailFile, setEditThumbnailFile] = useState<File | null>(null);
  const [editThumbnailPreview, setEditThumbnailPreview] = useState<string>("");
  const [editingSaving, setEditingSaving] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const editThumbnailInputRef = useRef<HTMLInputElement>(null);



  const pendingVideoIdRef = useRef<string>("");
  const previousVideoCountRef = useRef<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<VideoRecord | null>(null);
  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);



  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      console.log("📹 Video file selected:", e.target.files[0].name);
    }
  };




  const THUMBNAIL_CDN = "https://d3e4oen9k5mseu.cloudfront.net";



    const toCloudFrontUrl = (s3OrCdnUrl?: string) => {
      if (!s3OrCdnUrl) return "";
      if (s3OrCdnUrl.includes("cloudfront.net")) return s3OrCdnUrl;



      // extract path after bucket domain
      const idx = s3OrCdnUrl.indexOf(".amazonaws.com/");
      if (idx === -1) return s3OrCdnUrl;



      const path = s3OrCdnUrl.substring(idx + ".amazonaws.com/".length);
      return `${THUMBNAIL_CDN}/${path}`;
    };



  const handleThumbnailSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setThumbnail(file);



      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      console.log("🖼️ Thumbnail file selected:", file.name);
    }
  };



  const handleEditThumbnailSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setEditThumbnailFile(file);



      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      console.log("🖼️ Edit thumbnail file selected:", file.name);
    }
  };



  const sendVideoUploadNotification = async (videoTitle: string, stallIdParam: number, thumbnailUrl?: string,  videoId?: string) => {
  try {
    console.log("🔔 Preparing to send notification...");
    console.log("   - stallId:", stallIdParam);
    console.log("   - videoTitle:", videoTitle);
    console.log("   - thumbnailUrl:", thumbnailUrl);
    console.log("   - videoId:", videoId);
    console.log("🚀🚀🚀 SEND NOTIFICATION FUNCTION CALLED 🚀🚀🚀");

    const apiOptions = {
      url: `stall/${stallIdParam}`,
    };
    const stallResponse = await http.get(apiOptions);
    const stallName = stallResponse.data?.stallName || "Stall";
    console.log("   - stallName (fetched from API):", stallName);

    const notificationPayload = {
      stallName: stallName,
      thumbnailUrl: thumbnailUrl || null,// include thumbnail (may be null)
      videoId: videoId,        // ✅ Add videoId
      videoTitle: videoTitle
    };

    console.log("📤 Notification Payload:", notificationPayload);

    const notificationApiOptions = {
      url: `test/notifications/stall/${stallIdParam}/send-notification`,
      data: notificationPayload
    };

    const response = await http.post(notificationApiOptions);

    if (response.data?.DeepLink) {
      console.log("🔗 Deep link generated:", response.data.DeepLink);
    }

    console.log("✅ Notification sent successfully for new video:", videoTitle);
  } catch (err) {
    console.error("❌ Failed to send notification:", err);
    
  }
};



const fetchVideos = async (checkForNewVideo: boolean = false) => {
    console.log("🔄 Fetching videos... checkForNewVideo:", checkForNewVideo);
    setLoadingVideos(true);
    try {
      const apiOptions = {
        url: `uploads/list?stallId=${stallId}`,
      };
      const response = await http.get(apiOptions);
      const data: VideoRecord[] = response.data;

      // Log the response to verify likes are being returned
      console.log("📊 Videos fetched. Full response:", response.data);
      console.log("   - Count:", data.length);
      console.log("   - First video likes:", data[0]?.likes);
      console.log("   - First video views:", data[0]?.views);



      // ✅ CHANGED: Remove count check, only check if we have pending video ID
      if (checkForNewVideo && pendingVideoIdRef.current) {
        console.log("🆕 Checking for uploaded video by ID:", pendingVideoIdRef.current);



        const newVideo = data.find(v => v.id === pendingVideoIdRef.current);



        if (newVideo) {
          console.log("✅ Uploaded video found by ID!");
          console.log("   - Video ID:", newVideo.id);
          console.log("   - Video Title:", newVideo.title);
          console.log("   - Stall ID:", newVideo.stallId);



          await sendVideoUploadNotification(newVideo.title, stallId, toCloudFrontUrl(newVideo.thumbnailUrl), newVideo.id);
          pendingVideoIdRef.current = "";
          console.log("🔄 Reset pending video ID");
        } else {
          console.log("⚠️ Uploaded video not found yet. Processing might be delayed.");
        }
      }



      previousVideoCountRef.current = data.length;
      setVideos(data || []);
      console.log("✅ Videos state updated with likes data");
    } catch (err) {
      console.error("❌ Error fetching videos:", err);
      setVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  };



  useEffect(() => {
    console.log("🚀 Component mounted. stallId:", stallId);
    if (stallId) {
      fetchVideos();
    }
  }, [stallId]);



  const uploadThumbnailToS3 = async (thumbnailFile: File, videoId: string): Promise<string> => {
    console.log("📤 Uploading thumbnail to S3... videoId:", videoId);



    try {
      const uploadData = {
        fileName: thumbnailFile.name,
        contentType: thumbnailFile.type,
        contentLength: thumbnailFile.size,
        videoId: videoId,
        userId: userId
      };



      console.log("📦 Thumbnail presign data:", uploadData);



      const apiOptions = {
        url: `uploads/presign-thumbnail`,
        data: uploadData,
      };



      const response = await http.post(apiOptions);
      const presignData = getPresign(response.data);



      if (!presignData.url) {
        console.error("❌ Thumbnail presign URL missing in response:", response.data);
        throw new Error("Thumbnail presign URL missing in response");
      }



      console.log("🔗 Thumbnail presigned URL received");



      // Upload thumbnail to S3
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presignData.url, true);
        xhr.setRequestHeader("Content-Type", thumbnailFile.type);
        xhr.onload = () => {
          // accept any 2xx
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log("✅ Thumbnail uploaded to S3");
            resolve();
          } else {
            console.error("❌ Thumbnail S3 upload failed with status:", xhr.status);
            reject(new Error(`Thumbnail upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => {
          console.error("❌ Network error during thumbnail upload");
          reject(new Error("Network error"));
        };
        xhr.send(thumbnailFile);
      });



      return presignData.key;
    } catch (err) {
      console.error("❌ Thumbnail upload failed:", err);
      throw err;
    }
  };



  const handleUpload = async () => {
    console.log("📤 Upload initiated...");
    console.log("   - File:", file?.name);
    console.log("   - Thumbnail:", thumbnail?.name);
    console.log("   - Title:", title);
    console.log("   - Description:", description);



    if (!file) {
      AlertService.showErrorMsg("Please select a video file.");
      return;
    }
    if (!userId) {
      AlertService.showErrorMsg("userId is required!");
      return;
    }
    if (!stallId) {
      AlertService.showErrorMsg("stallId is required");
      return;
    }



    const MAX_BYTES = 100 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      AlertService.showErrorMsg("Upload blocked: Video size cannot exceed 100 MB.");
      return;
    }



    setUploading(true);
    setProgress(0);



    try {
      const uploadData = {
        fileName: file.name,
        contentType: file.type,
        contentLength: file.size,
        userId: userId,
        stallId: stallId,
        likes: 0,
        title,
        description,
      };



      console.log("📦 Upload data prepared:", uploadData);



      const apiOptions = {
        url: `uploads/presign`,
        data: uploadData,
      };



      const response = await http.post(apiOptions);



      // get presign (defensive to different property names)
      const presignData = getPresign(response.data);



      // extract videoId if backend returns it (new controller returns videoId)
      const returnedVideoId =
        response.data?.videoId ||
        response.data?.VideoId ||
        response.data?.videoID ||
        response.data?.VideoID ||
        "";



      // fallback: try to derive from key if available (legacy)
      const derivedVideoIdFromKey =
        presignData.key ? presignData.key.split("/").pop()?.split(".").shift() || "" : "";



      const videoIdToUse = returnedVideoId || derivedVideoIdFromKey;



      if (!presignData.url) {
        console.error("❌ Video presign URL missing in response:", response.data);
        throw new Error("Video presign URL missing in response");
      }



      console.log("🔗 Presigned URL received:", presignData.url, " videoId:", videoIdToUse);



      // Upload video to S3
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presignData.url, true);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const progressPercent = Math.round((e.loaded / e.total) * 100);
            setProgress(progressPercent);
            console.log("📊 Upload progress:", progressPercent + "%");
          }
        };
        xhr.onload = () => {
          // accept any 2xx
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log("✅ S3 upload successful");
            resolve();
          } else {
            console.error("❌ S3 upload failed with status:", xhr.status);
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => {
          console.error("❌ Network error during upload");
          reject(new Error("Network error"));
        };
        xhr.send(file);
      });



      // Upload thumbnail if selected
      if (thumbnail) {
        try {
          // Use returned videoId (preferred) or fallback derived id
          if (!videoIdToUse) {
            console.warn("⚠️ No videoId available from presign response; attempting to derive from presign key.");
          }
          await uploadThumbnailToS3(thumbnail, videoIdToUse);
        } catch (err) {
          console.error("⚠️ Video uploaded but thumbnail upload failed:", err);
          AlertService.showErrorMsg("Video uploaded but thumbnail upload failed. You can update it later.");
        }
      }



      pendingVideoIdRef.current = videoIdToUse;
      console.log("💾 Stored pending video ID:", videoIdToUse);



      setFile(null);
      setThumbnail(null);
      setThumbnailPreview("");
      setTitle("");
      setDescription("");



      if (videoInputRef.current) videoInputRef.current.value = "";
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";



      setShowProcessing(true);
      console.log("⏳ Video processing started. Waiting 60 seconds...");
      AlertService.showSuccessMsg("Video uploaded! Your video is processing and will appear soon.");



      setTimeout(() => {
        console.log("⏰ 60 seconds elapsed. Fetching videos to check for new upload...");
        setShowProcessing(false);
        fetchVideos(true);
      }, 60000);
    } catch (err) {
      console.error("❌ Upload failed:", err);
      AlertService.showErrorMsg("Upload failed: " + (err as Error).message);
      pendingVideoIdRef.current = "";
      console.log("🔄 Cleared pending video title due to error");
    } finally {
      setUploading(false);
      console.log("✅ Upload process completed");
    }
  };



  const handleEdit = (video: VideoRecord) => {
    console.log("✏️ Edit initiated for video:", video.id, video.title);
    
    // Set video editing state
    setEditingVideo(video);
    setEditTitle(video.title);
    setEditDescription(video.description);
    setEditThumbnailFile(null);
    setEditThumbnailPreview(toCloudFrontUrl(video.thumbnailUrl));
    setActiveMenu(null);
  };



  const handleCancelEdit = () => {
    console.log("❌ Edit cancelled");
    setEditingVideo(null);
    setEditTitle("");
    setEditDescription("");
    setEditThumbnailFile(null);
    setEditThumbnailPreview("");
    if (editThumbnailInputRef.current) editThumbnailInputRef.current.value = "";
  };



  const handleSaveEdit = async () => {
    console.log("💾 Save edit initiated...");
    console.log("   - Video ID:", editingVideo?.id);
    console.log("   - New Title:", editTitle);
    console.log("   - New Description:", editDescription);
    console.log("   - New Thumbnail File:", editThumbnailFile?.name);



    if (!editingVideo) {
      AlertService.showErrorMsg("No video selected for editing.");
      return;
    }
    if (!editTitle.trim()) {
      AlertService.showErrorMsg("Title is required.");
      return;
    }



    setEditingSaving(true);

    try {
      // Step 1: Update title and description
      const updatePayload = {
        videoId: editingVideo.id,
        userId: editingVideo.userId,
        title: editTitle.trim(),
        description: editDescription.trim(),
      };



      console.log("📤 Update payload:", updatePayload);



      const apiOptions = {
        url: `uploads/update`,
        data: updatePayload,
      };



      await http.put(apiOptions);
      console.log("✅ Video title and description updated");

      // Step 2: Upload new thumbnail if selected
      if (editThumbnailFile) {
        try {
          await uploadThumbnailToS3(editThumbnailFile, editingVideo.id);
          console.log("✅ Thumbnail updated");
        } catch (err) {
          console.error("⚠️ Title/description updated but thumbnail upload failed:", err);
          AlertService.showErrorMsg("Video updated but thumbnail upload failed. You can try again later.");
        }
      }

      // Step 3: Update local state
      setVideos((prevVideos) =>
        prevVideos.map((v) =>
          v.id === editingVideo.id
            ? { 
                ...v, 
                title: editTitle.trim(), 
                description: editDescription.trim(),
                thumbnailUrl: editThumbnailFile ? editThumbnailPreview : v.thumbnailUrl
              }
            : v
        )
      );

      setEditingVideo(null);
      setEditTitle("");
      setEditDescription("");
      setEditThumbnailFile(null);
      setEditThumbnailPreview("");
      if (editThumbnailInputRef.current) editThumbnailInputRef.current.value = "";
      
      AlertService.showSuccessMsg("Video updated successfully!");
    } catch (err) {
      console.error("❌ Failed to update video:", err);
      AlertService.showErrorMsg("Failed to update video. Please try again.");
    } finally {
      setEditingSaving(false);
    }
  };



  const openDeleteDialog = (video: VideoRecord) => {
    setActiveMenu(null);
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };



  const performDelete = async () => {
    if (!videoToDelete) return;
    const videoId = videoToDelete.id;



    setDeletingVideoId(videoId);



    try {
      const deletePayload = { userId, videoIds: [videoId] };
      await axiosInstance.delete("uploads/delete", {
        headers: { "Content-Type": "application/json" },
        data: deletePayload,
      });



      setVideos(prev => prev.filter(v => v.id !== videoId));
      AlertService.showSuccessMsg("Video deleted successfully!");
    } catch (err) {
      console.error("❌ Failed to delete video:", (err as Error).message);
      AlertService.showErrorMsg("Failed to delete video. Please try again.");
    } finally {
      setDeletingVideoId(null);
      setVideoToDelete(null);
      setDeleteDialogOpen(false);
    }
  };



  const toggleMenu = (e: React.MouseEvent, videoId: string) => {
    e.stopPropagation();
    setActiveMenu(activeMenu === videoId ? null : videoId);
    console.log("📋 Menu toggled for video ID:", videoId);
  };



  return (
    <div className="video-upload-drawer">
      <Header />
      <div className="video-upload-main">
        <div className="videos-sidebar">
          <div className="videos-list-title">
            <span>My Videos </span>
            <span className="videos-count">{videos.length}</span>
          </div>
          {loadingVideos ? (
            <div className="videos-loading">
              <div className="spinner"></div>
              <span>Loading videos...</span>
            </div>
          ) : videos.length === 0 && !showProcessing ? (
            <div className="videos-empty-state">
              <span className="videos-empty-icon">🎬</span>
              <div className="videos-empty-text">No videos yet.</div>
              <div className="videos-empty-subtext">Upload your first video to get started!</div>
            </div>
          ) : (
            <div className="videos-grid">
              {showProcessing && (
                <div className="video-card-wrapper processing-wrapper">
                  <div className="video-card">
                    <div className="video-thumb processing-thumb">
                      <div className="processing-spinner"></div>
                    </div>
                  </div>
                  <div className="video-info">
                    <div className="video-card-title">Processing...</div>
                  </div>
                </div>
              )}
              {videos.map((video) => (
                <div className="video-card-wrapper" key={video.id}>
                  <div className="video-card">
                    <div className="video-thumb">
                      {video.thumbnailUrl ? (
                        <div className="thumbnail-wrapper">
                          <img
                            src={toCloudFrontUrl(video.thumbnailUrl)}
                            alt={video.title}
                            className="thumbnail-image"
                          />
                        </div>
                      ) : (
                        <div className="thumbnail-wrapper">
                          <video
                            src={video.s3Url}
                            controls={false}
                            muted
                            poster=""
                            onMouseOver={(e) => {
                              const vid = e.currentTarget as HTMLVideoElement;
                              if (vid.paused) vid.play().catch(() => {});
                            }}
                            onMouseOut={(e) => {
                              const vid = e.currentTarget as HTMLVideoElement;
                              if (!vid.paused) vid.pause();
                            }}
                          />
                          <div className="video-overlay">
                            <button className="play-button">▶</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="video-info">
                    <div className="video-header">
                      <div className="video-card-title">{video.title || "Untitled"}</div>
                      <div className="video-menu-container">
                        <button
                          className="video-menu-btn"
                          onClick={(e) => toggleMenu(e, video.id)}
                          type="button"
                          title="More options"
                        >
                          ⋮
                        </button>
                        {activeMenu === video.id && (
                          <>
                            <div
                              className="video-menu-dropdown"
                              tabIndex={0}
                              onClick={e => e.stopPropagation()}
                            >
                              <button
                                className="menu-item"
                                type="button"
                                onClick={() => handleEdit(video)}
                                title="Edit video details"
                              >
                                <EditIcon />
                                <span>Edit</span>
                              </button>
                              <button
                                className="menu-item menu-item-danger"
                                type="button"
                                onClick={() => openDeleteDialog(video)}
                                title="Delete video"
                              >
                                <DeleteIcon />
                                <span>Delete</span>
                              </button>
                            </div>
                            <div
                              className="menu-backdrop"
                              onClick={() => setActiveMenu(null)}
                            />
                          </>
                        )}
                      </div>
                    </div>
                    <div className="video-card-meta">
                      <span className="video-views">{video.views ?? 0} views</span>
                      <span className="video-separator">•</span>
                      <div className="video-likes-container">
                        <HeartIcon size={14} color="#666" />
                        <span className="video-likes">{video.likes ?? 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>



        <div className="upload-form-area">
          <div className="form-header">
            <div className="drawer-title">
              {editingVideo
                ? "Edit Video"
                : "Upload New Video"}
            </div>
            {editingVideo && (
              <button
                className="btn-cancel-edit"
                onClick={handleCancelEdit}
                type="button"
              >
                ✕
              </button>
            )}
          </div>



          {editingVideo ? (
            <>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  className="form-input"
                  type="text"
                  maxLength={200}
                  placeholder="Enter video title"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input form-textarea"
                  maxLength={1000}
                  placeholder="Describe your video"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Current Thumbnail</label>
                {editThumbnailPreview && (
                  <div className="thumbnail-preview-container">
                    <img src={editThumbnailPreview} alt="Current thumbnail" className="thumbnail-preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Change Thumbnail</label>
                <div
                  className="upload-area thumbnail-upload-area"
                  onClick={() => editThumbnailInputRef.current?.click()}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    ref={editThumbnailInputRef}
                    onChange={handleEditThumbnailSelect}
                    style={{ display: "none" }}
                  />
                  <div className="upload-icon">
                    <SmallUploadIcon />
                  </div>
                  <span className="upload-highlight">Click to upload</span>
                  <span className="upload-info">PNG, JPG, WebP (9:16 ratio)</span>
                  {editThumbnailFile && (
                    <div className="upload-selected">
                      📷 {editThumbnailFile.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="drawer-actions">
                <button
                  className="drawer-btn drawer-btn--cancel"
                  type="button"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button
                  className="drawer-btn drawer-btn--publish"
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={editingSaving}
                >
                  {editingSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  className="form-input"
                  type="text"
                  maxLength={100}
                  placeholder="Enter video title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input form-textarea"
                  maxLength={500}
                  placeholder="Describe your video"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Video File</label>
                <div
                  className="upload-area"
                  onClick={() => videoInputRef.current?.click()}
                >
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/mov"
                    ref={videoInputRef}
                    onChange={handleVideoSelect}
                    style={{ display: "none" }}
                  />
                  <div className="upload-icon">
                    <CloudUploadIcon />
                  </div>
                  <span className="upload-highlight">Click to upload</span>
                  <span className="upload-info">MP4(max. 100MB)</span>
                  {file && (
                    <div className="upload-selected">
                      📹 {file.name}
                    </div>
                  )}
                  {uploading && (
                    <div className="upload-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">{progress}%</span>
                    </div>
                  )}
                </div>
              </div>



              <div className="form-group">
                <label className="form-label">Thumbnail</label>
                <div
                  className="upload-area thumbnail-upload-area"
                  onClick={() => thumbnailInputRef.current?.click()}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    ref={thumbnailInputRef}
                    onChange={handleThumbnailSelect}
                    style={{ display: "none" }}
                  />
                  <div className="upload-icon">
                    <SmallUploadIcon />
                  </div>
                  <span className="upload-highlight">Click to upload</span>
                  <span className="upload-info">PNG, JPG, WebP (9:16)</span>
                  {thumbnail && (
                    <div className="upload-selected">
                      📷 {thumbnail.name}
                    </div>
                  )}
                  {thumbnailPreview && (
                    <div className="thumbnail-preview-mini">
                      <img src={thumbnailPreview} alt="Thumbnail preview" />
                    </div>
                  )}
                </div>
              </div>



              <div className="drawer-actions">
                <button
                  className="drawer-btn drawer-btn--cancel"
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="drawer-btn drawer-btn--publish"
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading || !file || showProcessing}
                >
                  {uploading ? "Uploading..." : showProcessing ? "Processing..." : "Publish"}
                </button>
              </div>
            </>
          )}



          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle style={{ position: "relative" }}>
              Delete Video?
              <IconButton
                aria-label="close"
                onClick={() => setDeleteDialogOpen(false)}
                style={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>



            <DialogContent>
              <Typography>
                Are you sure you want to delete{" "}
                <b>{videoToDelete?.title || "this video"}</b>? This action cannot be undone.
              </Typography>
            </DialogContent>



            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                color="default"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={performDelete}
                color="secondary"
                variant="contained"
                disabled={!!deletingVideoId}
              >
                {deletingVideoId ? <CircularProgress size={18} /> : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </div>
  );
};



export default VideoUpload;
